import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import { LANGUAGES, changeLanguage } from "@/i18n";
import { LANGUAGE } from "@/constants/testIds";
import { useSheetHistory } from "@/hooks/useSheetHistory";

/**
 * The language control has two homes, sharing `LANGUAGES` and `changeLanguage`
 * from `@/i18n` but not a layout:
 *
 * - `LanguageSwitcher` (default export) — EN / RU / ID as one segmented pill.
 *   Lives in Profile settings, where a returning guest has room for three
 *   two-letter buttons.
 * - `LanguageMenu` (named export) — a compact trigger that opens a bottom
 *   sheet. Lives at the foot of every storefront screen (rendered by Shell),
 *   where it is always reachable without crowding any screen's header.
 */
const LanguageSwitcher = ({ className = "" }) => {
  const { t, i18n } = useTranslation();

  return (
    <div
      data-testid={LANGUAGE.switcher}
      role="group"
      aria-label={t("language.label")}
      className={`inline-flex items-center gap-0.5 rounded-full bg-neutral-100 p-1 ${className}`}
    >
      {LANGUAGES.map((lang) => {
        const active = i18n.language === lang.code;
        return (
          <button
            key={lang.code}
            data-testid={LANGUAGE.option(lang.code)}
            onClick={() => changeLanguage(lang.code)}
            aria-pressed={active}
            aria-label={lang.name}
            className={`px-2.5 h-7 rounded-full text-[11px] font-black tracking-wider transition-colors active:scale-[0.96] ${
              active ? "bg-neutral-900 text-white" : "text-neutral-500"
            }`}
          >
            {lang.short}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Compact trigger + bottom sheet — the globe at the foot of every screen.
 *
 * The trigger shows only the active language's short code — the full switch
 * happens in the sheet, where each row can afford its language's own native
 * name instead of a two-letter guess at it.
 */
export const LanguageMenu = ({ testId = LANGUAGE.switcher }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  useSheetHistory(open, () => setOpen(false));
  const active = LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  const pick = (code) => {
    changeLanguage(code);
    setOpen(false);
  };

  return (
    <>
      <button
        data-testid={testId}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={t("language.label")}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-neutral-100 text-neutral-800 text-[11px] font-black tracking-wider active:scale-[0.96] transition-transform"
      >
        <Globe size={14} />
        {active.short}
      </button>

      {open && (
        <div
          data-testid={LANGUAGE.sheet}
          className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("language.label")}
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-4 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 px-2 py-3">
              {t("language.label")}
            </div>
            {LANGUAGES.map((lang) => {
              const isActive = lang.code === i18n.language;
              return (
                <button
                  key={lang.code}
                  data-testid={LANGUAGE.option(lang.code)}
                  onClick={() => pick(lang.code)}
                  className="w-full min-h-[48px] flex items-center justify-between gap-3 px-2 rounded-xl text-left text-base font-semibold text-neutral-900 active:bg-neutral-50 transition-colors"
                >
                  {lang.name}
                  {isActive && <Check size={16} className="text-[#E34A27]" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default LanguageSwitcher;
